import type { PropsWithChildren } from "react";
import React, { useEffect, useState } from "react";

import { NoLicense } from "@/components/AppContext/License";
import { WithLicenseBanner } from "@/components/AppContext/License";
import { plans } from "@/lib/acli-plans";
import { useServiceContext } from "@/services";
import type { MondayPlan } from "@/services/monday";

import { useCurrentMondayContext } from "./WithMondayContextAvailable";

// TODO: Revisit this approach - https://appfire.atlassian.net/browse/ACLI-3362

// If license is valid, it will display the child components (our app content).
// If not it will replace the child components with a NoLicense page instead.
// If license is about to expire, it will display a banner with a message but still
// display the child components.
export function WithValidLicense({ children }: PropsWithChildren) {
  const { monday } = useServiceContext();
  const { subscription } = useCurrentMondayContext();
  const [plan, setPlan] = useState<MondayPlan>();
  // TODO: Find a way to assert this is set; if not it's a build/env config issue
  const disableLicenseChecks = import.meta.env.VITE_DISABLE_LICENSE_CHECKS === "true";

  useEffect(() => {
    monday
      .fetchMondayPlan()
      .then(setPlan)
      .catch((e) => {
        // TODO: proper error handling
        console.error(e);
      });
  }, [monday]);

  const daysLeft = subscription ? subscription.days_left : -1;
  const planMaxUsers = plan ? plan.max_users : 999999;
  const trial = subscription ? subscription.is_trial : false;
  const userLimitExceeded = plan && subscription ? plans[subscription.plan_id] < planMaxUsers : false;

  if (disableLicenseChecks) {
    return <>{children}</>;
  }

  if (daysLeft < 0) {
    return <LicenseExpired />;
  }

  if (trial && daysLeft < 7) {
    return <TrialAboutToExpire children={children} daysLeft={daysLeft} />;
  }

  if (!trial && userLimitExceeded) {
    return <UserLimitReached />;
  }

  return <>{children}</>;
}

function LicenseExpired() {
  return (
    <WithLicenseBanner message={bannerMessages.licenseRequired}>
      <NoLicense message={appBlockerMessages.unlicensed} />
    </WithLicenseBanner>
  );
}

function UserLimitReached() {
  return (
    <WithLicenseBanner message={bannerMessages.licenseRequired}>
      <NoLicense message={appBlockerMessages.userLimitExceeded} />
    </WithLicenseBanner>
  );
}

function TrialAboutToExpire({ children, daysLeft }: { children: React.ReactNode; daysLeft: number }) {
  return <WithLicenseBanner message={bannerMessages.trialAboutToExpire(daysLeft)}>{children}</WithLicenseBanner>;
}

// Exported for testing
export const bannerMessages = {
  licenseRequired: "License Required",
  trialAboutToExpire: (daysLeft: number) => {
    const at = daysLeft <= 0 ? "today" : daysLeft === 1 ? "tomorrow" : `in ${daysLeft} days`;
    return `Your trial will expire ${at}. Consider purchasing a license`;
  },
};

// Exported for testing
export const appBlockerMessages = {
  unlicensed: "CLI - Automate Actions is unlicensed. Consider purchasing a valid license.",
  userLimitExceeded:
    "You are running CLI - Automate Actions with fewer seats than you have in your account. Please upgrade the license to keep using it.",
};
